import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    Currency,
    PaymentProvider,
    PixAddressType,
    PixApiProvider,
    TranslationScopes,
} from "@finbackoffice/enums";
import { IExchangeRatePair, ISiteConfigs } from "@finbackoffice/clientbff-client";
import { useTranslation } from "react-i18next";
import { ClientBFFContext } from "../../providers";
import { getCurrencyConfig, round } from "../../helpers";
import { PixWalletDataContext } from "./pix-wallet-data.provider";

export const useFirstStepWithdrawal = ({
    siteConfigs,
    rates,
    balance,
}: {
    siteConfigs: ISiteConfigs;
    rates: Record<Currency, IExchangeRatePair[]> | null;
    balance: string;
}) => {
    const { t } = useTranslation(TranslationScopes.PixWallet);
    const client = useContext(ClientBFFContext);
    const { setWithdrawalStep, setWithdrawalData } = useContext(PixWalletDataContext);
    const [errorApi, setErrorApi] = useState<string[]>([]);
    const [exceededLimit, setExceededLimit] = useState<string>("");
    const [amount, setAmount] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [recipientData, setRecipientData] = useState<{
        type: PixAddressType;
        value: string;
        error?: string;
    }>({
        type: PixAddressType.CPF,
        value: "",
        error: "",
    });
    const brlRate = rates?.[Currency.BRL]?.find((r) => r.toCurrency === "USD")?.rate;
    const currencyConfig = getCurrencyConfig(siteConfigs, Currency.BRL);
    const { min, max_usd } = currencyConfig.withdrawal || {};
    const max = brlRate && max_usd ? max_usd / Number(brlRate) : 0;
    const roundedMax = round(max, currencyConfig.digits);
    const maxWithdrawal = Math.min(Number(balance), roundedMax);

    const withdrawalProviders = useMemo(() => {
        const providersData = (siteConfigs as ISiteConfigs).payments.PixAPI?.withdrawal || {};
        return (Object.keys(providersData) as PixApiProvider[])
            .map((provider) => ({
                provider,
                ...providersData[provider]!,
            }))
            .filter((p) => p.enabled)
            .sort((a, b) => a.order - b.order);
    }, [siteConfigs]);

    const [selectedProvider, setSelectedProvider] = useState<PixApiProvider>(
        withdrawalProviders[0]?.provider,
    );

    useEffect(() => {
        setErrorApi([]);
    }, [recipientData.value, amount, selectedProvider]);

    useEffect(() => {
        setSelectedProvider(withdrawalProviders[0]?.provider);
    }, [withdrawalProviders]);

    useEffect(() => {
        const numberValue = Number(amount || 0);
        if (!amount) {
            setError("");
            return;
        }
        if (numberValue < (min ?? 0) || numberValue > maxWithdrawal) {
            setError(t("min_max_validation", { min, max: maxWithdrawal }));
        } else {
            setError("");
        }
    }, [amount, min, maxWithdrawal, t]);

    const clickWithdrawal = useCallback(async () => {
        setErrorApi([]);
        setExceededLimit("");
        try {
            setLoading(true);
            const response = await client.createWithdrawal({
                provider: PaymentProvider.PixAPI,
                currency: Currency.BRL,
                amount,
                provider_data: {
                    pix_api_provider: selectedProvider,
                    ...(recipientData.type !== PixAddressType.CPF && {
                        provider_data: {
                            recipient_address: {
                                type: recipientData.type,
                                value: recipientData.value,
                            },
                        },
                    }),
                },
            });
            setWithdrawalData(response);
            setWithdrawalStep("complete");
        } catch (e: any) {
            const errors = e.response.data?.details ?? {};
            if (errors.max_24h_count_withdrawal_error) {
                setExceededLimit(errors.max_24h_count_withdrawal_error);
                return;
            }
            if (e.response.data?.error === "wallet_transient_exception") {
                setErrorApi([t("error_wallet_transient_exception")]);
                return;
            }
            if (e.response.data?.error) {
                let detailedMessage = [e.response.data?.message];
                const details = e.response.data?.details;
                if (details) {
                    if (details.max_deposit_error) {
                        detailedMessage = [details.max_deposit_error];
                    } else {
                        for (const key in details) {
                            detailedMessage.push(details[key]);
                        }
                    }
                }

                if (detailedMessage.length > 0) {
                    setErrorApi(detailedMessage);
                    return;
                }
            }
            if (errors.error_message) {
                setErrorApi([errors.error_message]);
                return;
            }
            setErrorApi([e.response.data?.message || e.message]);
        } finally {
            setLoading(false);
        }
    }, [amount, client, recipientData, setWithdrawalData, setWithdrawalStep, t, selectedProvider]);

    return useMemo(
        () => ({
            clickWithdrawal,
            amount,
            error,
            loading,
            min,
            setAmount,
            recipientData,
            setRecipientData,
            errorApi,
            exceededLimit,
            withdrawalProviders,
            selectedProvider,
            setSelectedProvider,
        }),
        [
            clickWithdrawal,
            amount,
            error,
            loading,
            setAmount,
            min,
            recipientData,
            setRecipientData,
            errorApi,
            exceededLimit,
            withdrawalProviders,
            selectedProvider,
            setSelectedProvider,
        ],
    );
};
